import React, { useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';

const getPalCodeName = (pc) => {
    if (typeof pc === 'string') return pc;
    return pc?.palCodeName || pc?.PalCodeName || '';
};

const getPalCodeId = (pc) => {
    if (typeof pc === 'string') return pc;
    return pc?.palCodeId || pc?.PalCodeId || getPalCodeName(pc);
};

const normalizeMissingPalCodes = (list) =>
    (list ?? []).map(pc => ({
        palCodeId: getPalCodeId(pc),
        palCodeName: getPalCodeName(pc) || getPalCodeId(pc),
    })).filter(pc => pc.palCodeName);

const RamplusAccountCardsImportModal = ({
    open,
    onClose,
    previewRamplusAccountCards,
    commitRamplusAccountCards,
    createMissingPalCode,
    direction,
    t,
    palCodeCategoryOptions = [],
    palCodes = [],
    Constants = {},
    user
}) => {
    const customStyles = {
        container: (provided) => ({ ...provided, width: '100%', boxSizing: 'border-box !important' }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            '&:focus-within': { border: '2px solid var(--Foundation-Blue-Normal, #304FFF)' },
        }),
        placeholder: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        singleValue: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        menu: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#F9AA2A' : state.isFocused ? '#ebebeb' : null,
            color: 'black',
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 2002 }),
    };

    const palCodeTypeOptions = (Constants.PalCodeType || []).map(type => ({ label: type, value: type }));
    const palCodeCategorySelectOptions = palCodeCategoryOptions.map(cat => ({
        label: cat.palCodeCategoryId,
        value: cat.palCodeCategoryId
    }));

    const [cardsFile, setCardsFile] = useState(null);
    const [accountsFile, setAccountsFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [parsedRows, setParsedRows] = useState(0);
    const [accountCards, setAccountCards] = useState([]);
    const [missingPalCodes, setMissingPalCodes] = useState([]);
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({
        palCodeId: '',
        palCodeType: '',
        palCodeCategory: null
    });
    const [bulkForm, setBulkForm] = useState({
        palCodeType: '',
        palCodeCategory: null
    });

    const calculateNextOrderNumber = (category) => {
        if (!category || palCodes.length === 0) return 100;
        const maxOrderNumber = palCodes
            .filter(pc => pc.palCodeCategory === category)
            .reduce((max, pc) => Math.max(max, pc.orderNumber || 0), 0);
        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    };

    const missingPalCodeNames = missingPalCodes.map(pc => pc.palCodeName);

    const handlePreview = async () => {
        if (!cardsFile) return;
        try {
            setProcessing(true);
            const res = await previewRamplusAccountCards(cardsFile, accountsFile);
            setAccountCards(res?.accountCards ?? []);
            setMissingPalCodes(normalizeMissingPalCodes(res?.missingPalCodes));
            setParsedRows(res?.parsedRows ?? 0);
            setPreviewDone(true);
            setEditingPalCode(null);
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
        } catch (err) {
            console.error('Ramplus account cards preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const startEditingPalCode = (pc) => {
        setEditingPalCode(pc.palCodeName);
        setPalCodeForm({
            palCodeId: pc.palCodeId || pc.palCodeName,
            palCodeType: '',
            palCodeCategory: null
        });
    };

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) return;

        try {
            setProcessing(true);
            const response = await createMissingPalCode({
                palCodeId: palCodeForm.palCodeId,
                palCodeType: palCodeForm.palCodeType,
                palCodeCategory: palCodeForm.palCodeCategory,
                orderNumber: palCodeForm.palCodeCategory
                    ? calculateNextOrderNumber(palCodeForm.palCodeCategory)
                    : 100,
                accountantId: user?.accountantId,
            });
            if (!response.ok) throw new Error(await response.text());

            setMissingPalCodes(prev => prev.filter(pc => pc.palCodeName !== palCodeName));
            setEditingPalCode(null);
            setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('PalCode creation error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!bulkForm.palCodeType || selectedPalCodesForBulk.length === 0) return;

        try {
            setProcessing(true);
            const baseOrderNumber = bulkForm.palCodeCategory
                ? calculateNextOrderNumber(bulkForm.palCodeCategory)
                : 100;

            const selectedItems = missingPalCodes.filter(pc =>
                selectedPalCodesForBulk.includes(pc.palCodeName)
            );

            const responses = await Promise.all(selectedItems.map((pc, index) =>
                createMissingPalCode({
                    palCodeId: pc.palCodeId || pc.palCodeName,
                    palCodeType: bulkForm.palCodeType,
                    palCodeCategory: bulkForm.palCodeCategory,
                    orderNumber: baseOrderNumber + (index * 100),
                    accountantId: user?.accountantId,
                })
            ));

            const failed = responses.filter(r => !r.ok);
            if (failed.length > 0) {
                throw new Error(`${failed.length} PalCodes failed to create`);
            }

            setMissingPalCodes(prev =>
                prev.filter(pc => !selectedPalCodesForBulk.includes(pc.palCodeName))
            );
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
            setBulkForm({ palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('Bulk PalCode creation error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!cardsFile) return;
        try {
            setProcessing(true);
            const res = await commitRamplusAccountCards(cardsFile, accountsFile);
            if (res?.errorMessages?.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Ramplus account cards commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setCardsFile(null);
        setAccountsFile(null);
        setPreviewDone(false);
        setAccountCards([]);
        setMissingPalCodes([]);
        setParsedRows(0);
        setEditingPalCode(null);
        setBulkMode(false);
        setSelectedPalCodesForBulk([]);
        setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        setBulkForm({ palCodeType: '', palCodeCategory: null });
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportRamplusAccountCards') || 'רם פלוס - כרטיסי חשבון'}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div>
                            <h5>{t('RamplusAccountCardsFile') || 'חשבוניות וקודי מיון (.xls)'}</h5>
                            <p style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                                {t('RamplusAccountCardsFormat') || 'עמודות: שם חשבון, מפתח חשבון, סעיף מאזני/תוצאתי'}
                            </p>
                            <DropzoneArea
                                key={cardsFile ? cardsFile.name : 'ramplus-cards-empty'}
                                onDrop={files => files?.length && setCardsFile(files[0])}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {cardsFile && <div style={{ marginTop: 8 }}>{t('FileSelected')}: {cardsFile.name}</div>}
                        </div>
                        <div>
                            <h5>{t('RamplusAccountsFileOptional') || 'חשבונות (מספר זהות/תיק) — אופציונלי (.xls)'}</h5>
                            <DropzoneArea
                                key={accountsFile ? accountsFile.name : 'ramplus-accounts-empty'}
                                onDrop={files => files?.length && setAccountsFile(files[0])}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {accountsFile && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {accountsFile.name}</span>
                                    <Button size="small" variant="text" onClick={() => setAccountsFile(null)}>{t('Remove')}</Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button variant="contained" onClick={handlePreview} disabled={!cardsFile || processing}>
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingPalCodes.length > 0}
                            onClick={handleCommit}
                        >
                            {processing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: 14, color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{parsedRows}</strong>
                                    {' | '}{t('AccountCards') || 'כרטיסי חשבון'}: <strong>{accountCards.length}</strong>
                                </p>
                            </div>

                            {missingPalCodes.length > 0 && (
                                <div style={{ marginBottom: 25, padding: 15, backgroundColor: '#fff3cd', borderRadius: 5, border: '1px solid #ffc107' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                        <div>
                                            <h5 style={{ color: '#856404', margin: 0 }}>
                                                {t('MissingPalCodes') || 'חשבונות ראשיים חסרים'}
                                            </h5>
                                            <p style={{ fontSize: 14, color: '#856404', margin: '5px 0 0 0' }}>
                                                {t('MissingPalCodesDescription') || 'יש ליצור את החשבונות הראשיים הבאים לפני הייבוא:'}
                                            </p>
                                        </div>
                                        <Button
                                            variant={bulkMode ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => {
                                                setBulkMode(!bulkMode);
                                                setSelectedPalCodesForBulk([]);
                                                setEditingPalCode(null);
                                                if (!bulkMode) {
                                                    setBulkForm({ palCodeType: '', palCodeCategory: null });
                                                }
                                            }}
                                        >
                                            {bulkMode ? (t('CancelBulk') || 'ביטול יצירה מרובה') : (t('BulkCreate') || 'יצירה מרובה')}
                                        </Button>
                                    </div>

                                    {bulkMode && (
                                        <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                                            <h6 style={{ marginBottom: 10 }}>{t('BulkCreatePalCodes') || 'יצירת חשבונות ראשיים מרובים'}</h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                        {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Select
                                                        placeholder={t('Select') || 'בחר'}
                                                        options={palCodeTypeOptions}
                                                        value={palCodeTypeOptions.find(opt => opt.value === bulkForm.palCodeType) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeType: selectedOption?.value || '' });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                        {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                    </label>
                                                    <Select
                                                        placeholder={t('None') || 'ללא'}
                                                        options={palCodeCategorySelectOptions}
                                                        value={palCodeCategorySelectOptions.find(opt => opt.value === bulkForm.palCodeCategory) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeCategory: selectedOption?.value || null });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        isClearable
                                                    />
                                                </div>
                                                <div style={{ marginTop: 10 }}>
                                                    <p style={{ fontSize: 12, color: '#666', marginBottom: 5 }}>
                                                        {t('SelectedPalCodes') || 'נבחרו'}: {selectedPalCodesForBulk.length} / {missingPalCodes.length}
                                                    </p>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={handleBulkCreatePalCodes}
                                                        disabled={processing || !bulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                                        fullWidth
                                                    >
                                                        {t('CreateSelected') || `צור ${selectedPalCodesForBulk.length} חשבונות`}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {missingPalCodes.map((pc, idx) => (
                                        <div key={`${pc.palCodeName}-${idx}`} style={{ marginBottom: 15, padding: 10, backgroundColor: '#fff', borderRadius: 3 }}>
                                            {editingPalCode === pc.palCodeName ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                    <TextField
                                                        label={t('PalCodeId') || 'מזהה חשבון ראשי'}
                                                        value={palCodeForm.palCodeId}
                                                        onChange={(e) => setPalCodeForm({ ...palCodeForm, palCodeId: e.target.value })}
                                                        size="small"
                                                        fullWidth
                                                    />
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                            {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        <Select
                                                            placeholder={t('Select') || 'בחר'}
                                                            options={palCodeTypeOptions}
                                                            value={palCodeTypeOptions.find(opt => opt.value === palCodeForm.palCodeType) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeType: selectedOption?.value || '' });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                            {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                        </label>
                                                        <Select
                                                            placeholder={t('None') || 'ללא'}
                                                            options={palCodeCategorySelectOptions}
                                                            value={palCodeCategorySelectOptions.find(opt => opt.value === palCodeForm.palCodeCategory) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeCategory: selectedOption?.value || null });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                            isClearable
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 10 }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleCreatePalCode(pc.palCodeName)}
                                                            disabled={processing || !palCodeForm.palCodeId || !palCodeForm.palCodeType}
                                                        >
                                                            {t('CreatePalCode') || t('create') || 'צור'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingPalCode(null);
                                                                setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
                                                            }}
                                                        >
                                                            {t('Cancel') || 'ביטול'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        {bulkMode && (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPalCodesForBulk.includes(pc.palCodeName)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedPalCodesForBulk(prev => [...prev, pc.palCodeName]);
                                                                    } else {
                                                                        setSelectedPalCodesForBulk(prev => prev.filter(name => name !== pc.palCodeName));
                                                                    }
                                                                }}
                                                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                            />
                                                        )}
                                                        <div>
                                                            <span style={{ fontWeight: 'bold' }}>{pc.palCodeName}</span>
                                                            {pc.palCodeId && pc.palCodeId !== pc.palCodeName && (
                                                                <span style={{ marginInlineStart: 8, color: '#666', fontSize: 13 }}>
                                                                    ({pc.palCodeId})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {!bulkMode && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => startEditingPalCode(pc)}
                                                        >
                                                            {t('CreatePalCode') || 'צור חשבון ראשי'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {accountCards.length === 0 ? (
                                <p style={{ color: '#666' }}>{t('NoAccountCardsFound') || 'לא נמצאו כרטיסי חשבון'}</p>
                            ) : (
                                <>
                                    <h5>{t('AccountCards') || 'כרטיסי חשבון'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: 10, textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardId') || 'מזהה כרטיס'}</th>
                                                <th style={{ padding: 10, textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardName') || 'שם הכרטיס'}</th>
                                                <th style={{ padding: 10, textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('PalCode') || 'חשבון ראשי'}</th>
                                                <th style={{ padding: 10, textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('AccountCaseId') || 'מס.ע.מ'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accountCards.map((entry, i) => {
                                                const isMissingPalCode = entry.palCode && missingPalCodeNames.includes(entry.palCode);
                                                return (
                                                    <tr
                                                        key={`${entry.cardId}-${i}`}
                                                        style={{
                                                            backgroundColor: isMissingPalCode ? '#fff3cd' : (i % 2 === 0 ? '#fff' : '#f9f9f9'),
                                                        }}
                                                    >
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardId || ''}</td>
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardName || ''}</td>
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left', color: isMissingPalCode ? '#856404' : 'inherit' }}>
                                                            {entry.palCode || ''}
                                                            {isMissingPalCode && <span style={{ color: '#dc3545', marginInlineStart: 5 }}>*</span>}
                                                        </td>
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.accountCaseId || ''}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RamplusAccountCardsImportModal;
